/**
 * CompanyActionCell - Per-row dropdown for setting company action (Custom/Exclude)
 *
 * Used in Step 2 (ReviewPending) and Step 3 (FinishMatching) for unmatched companies.
 * Allows users to set each unmatched company as:
 * - Exclude (default) - company will be skipped
 * - Custom: [type] - company will be created as user-generated with selected type
 */
import { Select } from 'components/UI/Select/Select'
import { useMemo, useCallback } from 'react'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel } from '../ImporterViewModel'
import { setAsCustomCompany, setAsExcluded } from '../importDataTransforms'

// Special value for "Exclude" option
const EXCLUDE_VALUE = '__exclude__'

interface CompanyActionCellProps {
  row: CSVRow
  /** If true, show a compact version for table cells */
  compact?: boolean
  /** Optional callback when action changes */
  onChange?: () => void
}

export function CompanyActionCell({
  row,
  compact = false,
  onChange,
}: CompanyActionCellProps) {
  const viewModel = useImporterViewModel()
  const companyHash = row[InternalColumns.COMPANY_HASH]

  // Build dropdown options
  const options = useMemo(() => {
    const items = [
      { label: 'Exclude', value: EXCLUDE_VALUE },
      ...(viewModel.companySubcategories?.map((subcat) => ({
        label: `Custom: ${subcat.category_name} / ${subcat.subcategory_name}`,
        value: `${subcat.category_id}-${subcat.value}`,
      })) ?? []),
    ]
    return items
  }, [viewModel.companySubcategories])

  // Get current selected value from row data
  const selectedValue = useMemo(() => {
    const isCustom = row[InternalColumns.IS_CUSTOM_COMPANY]
    if (!isCustom) return EXCLUDE_VALUE

    const categoryId = row[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]
    const subcategoryId = row[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]
    if (categoryId && subcategoryId) {
      return `${categoryId}-${subcategoryId}`
    }
    return EXCLUDE_VALUE
  }, [row])

  // Handle selection change
  const handleSelect = useCallback(
    (value: string | null) => {
      if (!value) return

      if (value === EXCLUDE_VALUE) {
        viewModel.updateImportData((data) => setAsExcluded(data, companyHash))
      } else {
        const [categoryId, subcategoryId] = value.split('-').map(Number)
        viewModel.updateImportData((data) =>
          setAsCustomCompany(data, companyHash, categoryId, subcategoryId)
        )
      }

      onChange?.()
    },
    [companyHash, viewModel, onChange]
  )

  return (
    <Select
      className={compact ? 'w-48' : 'w-64'}
      items={options}
      selected={selectedValue}
      onSelect={handleSelect}
      buttonSize="sm"
    />
  )
}

/**
 * Get display label for a row's current action
 */
export function getActionLabel(
  row: CSVRow,
  companySubcategories?: Array<{
    category_id: number
    value: number
    category_name: string
    subcategory_name: string
  }>
): string {
  const isCustom = row[InternalColumns.IS_CUSTOM_COMPANY]
  if (!isCustom) return 'Exclude'

  const categoryId = row[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID]
  const subcategoryId = row[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID]

  const subcat = companySubcategories?.find(
    (s) => s.category_id === categoryId && s.value === subcategoryId
  )

  if (subcat) {
    return `Custom: ${subcat.category_name} / ${subcat.subcategory_name}`
  }

  return 'Custom'
}
